import React from "react";
import { useParams } from "react-router-dom";

import useHttp from "../../../hooks/http.hook";

import "./StatsGrid.css";

const EMPTY_STATS = {
  soldProducts: "—",
  placedProducts: "—",
  totalOrders: "—",
  canceledOrders: "—",
  investmentAmount: "—",
  incomeAmount: "—",
  expenseAmount: "—",
};

const STAT_CARDS = [
  { key: "soldProducts", title: "Продано шт." },
  { key: "placedProducts", title: "Оформлено шт." },
  { key: "totalOrders", title: "Заказы" },
  { key: "canceledOrders", title: "Отменено" },
  { key: "investmentAmount", title: "Инвестиции" },
  { key: "incomeAmount", title: "Выручка" },
  { key: "expenseAmount", title: "Расходы" },
];

const formatStatNumber = (value) => {
  const n = Number(value ?? 0);
  return new Intl.NumberFormat("ru-RU", {
    useGrouping: true,
    maximumFractionDigits: 2,
  }).format(Number.isFinite(n) ? n : 0);
};

function StatsGrid({ periodId = 1 }) {
  const { requestWithMeta } = useHttp();
  const params = useParams();
  const statsPromiseRef = React.useRef(null);
  const lastRequestFnRef = React.useRef(null);
  const lastBranchKeyRef = React.useRef(null);
  const lastPeriodIdRef = React.useRef(null);
  const [loading, setLoading] = React.useState(true);

  const [stats, setStats] = React.useState(EMPTY_STATS);

  React.useEffect(() => {
    let cancelled = false;

    const branchKey = params.branchId;
    const period = Number(periodId) || 1;

    if (!branchKey) {
      setLoading(false);
      return undefined;
    }

    if (lastRequestFnRef.current !== requestWithMeta) {
      lastRequestFnRef.current = requestWithMeta;
      statsPromiseRef.current = null;
      lastBranchKeyRef.current = branchKey;
      lastPeriodIdRef.current = period;
    }

    if (
      lastBranchKeyRef.current !== branchKey ||
      lastPeriodIdRef.current !== period
    ) {
      lastBranchKeyRef.current = branchKey;
      lastPeriodIdRef.current = period;
      statsPromiseRef.current = null;
    }

    setLoading(true);
    setStats(EMPTY_STATS);

    const statsPromise =
      statsPromiseRef.current ??
      (statsPromiseRef.current = requestWithMeta(
        `/operator/stats/branch/${branchKey}?periodId=${period}`,
        "GET"
      ));

    (async () => {
      try {
        const res = await statsPromise;
        if (cancelled) return;
        if (!res?.ok) return;

        const data = res?.data;
        const s = data?.stats || {};

        setStats({
          soldProducts: formatStatNumber(s?.soldQuantity),
          placedProducts: formatStatNumber(s?.placedQuantity),
          totalOrders: formatStatNumber(s?.totalOrders),
          canceledOrders: formatStatNumber(s?.canceledOrders),
          investmentAmount: formatStatNumber(s?.investmentAmount),
          incomeAmount: formatStatNumber(s?.soldRevenue),
          expenseAmount: formatStatNumber(s?.expenseAmount),
        });
      } catch (_) {
        // view-first: keep placeholders
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [requestWithMeta, params.branchId, periodId]);

  return (
    <section className="op-stats" aria-label="Статистика" aria-busy={loading}>
      {loading
        ? STAT_CARDS.map((card) => (
            <div
              key={card.key}
              className="op-statCard op-statCard--skeleton"
              aria-hidden="true"
            >
              <div className="op-skeleton op-statTitleSkeleton" />
              <div className="op-skeleton op-statValueSkeleton" />
            </div>
          ))
        : STAT_CARDS.map((card) => (
            <div key={card.key} className="op-statCard">
              <div className="op-statTitle">{card.title}</div>
              <div className="op-statValue">{stats?.[card.key] ?? "—"}</div>
            </div>
          ))}
    </section>
  );
}

export default StatsGrid;
